import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { reducerType } from "../../redux/reducers";
import { setAdditionalAssetContent } from "../../redux/reducers/AdditionalAssets";
import { getAssetContentUrl } from "../api/assets/assets";
import { triggerBrowserDownload } from "../components/download/triggerBrowserDownload";

interface UseAdditionalAssetContentOptions {
  projectId: string | undefined;
  /** Project-relative asset path, e.g. `research/notes.md`. */
  path: string;
  /** Whether this asset is renderable as text (drives whether content loads). */
  isRenderable: boolean;
  /** Skip fetching (e.g. tab not yet opened / new-project flow). */
  skip?: boolean;
}

interface UseAdditionalAssetContentResult {
  /** Rendered text content for renderable assets, undefined otherwise/until loaded. */
  content: string | undefined;
  /** Whether content is currently being fetched. */
  isLoading: boolean;
  /** Whether the fetch failed (e.g. the asset 404'd between list and fetch). */
  isError: boolean;
  /**
   * Downloads the asset to the user's browser. Works for any type (renderable or
   * not) — used by the unsupported-asset view's Download button.
   */
  download: () => Promise<void>;
}

/**
 * Fetches a single additional asset addressed by its project-relative path.
 *
 * For renderable assets it fetches the presigned URL, reads the blob as text,
 * and caches the result in the `AdditionalAssets` slice (same blob→text flow as
 * `useProjects.fetchProjectAsset`). For non-renderable assets it does not load
 * content; callers use {@link UseAdditionalAssetContentResult.download} instead.
 */
export const useAdditionalAssetContent = ({
  projectId,
  path,
  isRenderable,
  skip = false,
}: UseAdditionalAssetContentOptions): UseAdditionalAssetContentResult => {
  const dispatch = useDispatch();

  const content = useSelector((state: reducerType) =>
    projectId
      ? state.AdditionalAssets.byProject[projectId]?.content[path]
      : undefined,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Track which (project, path) we've fetched so we don't refetch each render.
  const attemptedRef = useRef<string | undefined>(undefined);

  const fetchPresignedUrl = useCallback(async (): Promise<string> => {
    if (!projectId) throw new Error("projectId is required");
    const response = await getAssetContentUrl({ projectId, path });
    if (!response?.success || !response.data.presignedUrl) {
      throw new Error("Failed to get presigned content URL");
    }
    return response.data.presignedUrl;
  }, [projectId, path]);

  const doFetch = useCallback(async () => {
    if (!projectId || skip || !isRenderable) return;
    const key = `${projectId}:${path}`;
    if (attemptedRef.current === key) return;
    attemptedRef.current = key;

    setIsLoading(true);
    setIsError(false);
    try {
      const presignedUrl = await fetchPresignedUrl();
      const assetResponse = await fetch(presignedUrl);
      if (!assetResponse.ok) {
        throw new Error("Failed to fetch asset content from S3");
      }
      const text = await assetResponse.text();
      dispatch(setAdditionalAssetContent({ projectId, path, content: text }));
    } catch (error) {
      console.error(`Failed to load additional asset ${path}:`, error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, path, isRenderable, skip, dispatch, fetchPresignedUrl]);

  useEffect(() => {
    if (content === undefined) {
      doFetch();
    }
  }, [content, doFetch]);

  const download = useCallback(async () => {
    if (!projectId) return;
    // Fetch the blob first (rather than pointing the anchor at the presigned
    // URL) because browsers ignore the `download` attribute for cross-origin
    // hrefs — same reason DownloadProjectButton fetches before downloading.
    const presignedUrl = await fetchPresignedUrl();
    const assetResponse = await fetch(presignedUrl);
    if (!assetResponse.ok) {
      throw new Error("Failed to fetch asset for download");
    }
    const blob = await assetResponse.blob();
    // Download under the asset's filename (last path segment).
    const filename = path.slice(path.lastIndexOf("/") + 1);
    triggerBrowserDownload(blob, filename);
  }, [projectId, path, fetchPresignedUrl]);

  return { content, isLoading, isError, download };
};
